import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Globe, Zap, TrendingUp } from "lucide-react";
import { SectionTitle } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import PlaybookCta from "@/components/site/PlaybookCta";
import { UNDERSERVED_MARKETS, getKeyword } from "@/lib/keywords";
import { getPricingConfig } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

/**
 * Keyword landing page: /services/:slug for the 10 high-intent service
 * keywords. Content comes from lib/keywords.js; this template renders the
 * structure (deliverables, deep-dive sections, underserved markets, FAQs,
 * related services, CTAs).
 */
export default function KeywordLanding({ kw }) {
  const [cfg, setCfg] = useState(null);
  const { fmt } = useCurrency();
  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: kw.h1.replace(/\.$/, ""),
    provider: { "@type": "Person", name: "Julius Mason", url: "https://juliusmason.com" },
    areaServed: "Worldwide",
    description: kw.description,
    offers: { "@type": "Offer", priceCurrency: "USD", price: "200", description: "Websites starting at $200" },
  };
  const related = (kw.related || []).map(getKeyword).filter(Boolean);

  return (
    <div data-testid={`keyword-${kw.slug}`}>
      <SEO
        title={kw.title}
        description={kw.description}
        path={`/services/${kw.slug}`}
        lang="en"
        keywords={kw.metaKeywords}
        schema={[
          serviceSchema,
          faqSchema(kw.faqs),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: kw.h1.replace(/\.$/, ""), path: `/services/${kw.slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-20 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <nav className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6" aria-label="Breadcrumb" data-testid="keyword-breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/services" className="hover:text-primary">Services</Link> / <span className="text-foreground">{kw.h1.replace(/\.$/, "")}</span>
          </nav>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl" data-testid="keyword-h1">{kw.h1}</h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">{kw.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="keyword-quote-cta" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
              Get My Instant Quote <Zap className="w-4 h-4" />
            </Link>
            <Link to="/free-website-audit" data-testid="keyword-audit-cta" className="inline-flex items-center gap-2 border border-border px-7 py-3.5 font-medium hover:border-primary hover:text-primary active:scale-95 transition">
              Free Website Audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Remote service · Available worldwide · Transparent pricing · You own everything
          </p>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="What's Included" title="Exactly what you get." />
          <div className="grid sm:grid-cols-2 gap-4 -mt-4" data-testid="keyword-deliverables">
            {kw.deliverables.map((d, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-3 border border-border bg-card px-5 py-4 h-full" data-testid={`keyword-deliverable-${i}`}>
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground leading-relaxed">{d}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deep-dive sections */}
      <section className="py-16 md:py-24 border-y border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 space-y-16">
          {kw.sections.map((s, i) => (
            <div key={i} className="grid lg:grid-cols-12 gap-8" data-testid={`keyword-section-${i}`}>
              <div className="lg:col-span-4">
                <SectionTitle overline={`0${i + 1}`} title={s.h} />
              </div>
              <p className="lg:col-span-7 text-muted-foreground text-lg leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      {cfg && (
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <SectionTitle overline="Transparent Pricing" title="See your number before you commit." />
              <p className="text-muted-foreground leading-relaxed -mt-4 max-w-xl">
                Every price is published and itemised — pages, features and timeline each carry a
                visible price, so the estimate you build yourself is the quote we discuss.
              </p>
            </div>
            <div className="space-y-3" data-testid="keyword-pricing">
              {cfg.packages.map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-border bg-card px-5 py-4">
                  <span className="font-medium">{p.name}</span>
                  <span className="font-mono text-primary">{p.custom ? `${fmt(p.price_from)}+` : `from ${fmt(p.price_from)}`}</span>
                </div>
              ))}
              <Link to="/pricing" className="inline-flex items-center gap-2 text-primary font-medium link-underline" data-testid="keyword-pricing-link">
                Price my exact project in 60 seconds <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Underserved markets — where demand outstrips supply */}
      <section className="py-16 md:py-24 border-y border-border bg-secondary/20" data-testid="keyword-markets">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="max-w-3xl mb-10">
            <SectionTitle overline="Where I Work" title="High demand, short supply — my favourite kind of market." />
            <p className="text-muted-foreground leading-relaxed -mt-4">
              I work worldwide, but businesses in these markets often struggle to find senior,
              fairly priced web development locally. Remote delivery closes that gap — same quality,
              direct communication, priced in your currency. Each page below adapts automatically
              to your language and region.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {UNDERSERVED_MARKETS.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.05}>
                <Link to={`/web-design/${m.slug}`} data-testid={`keyword-market-${m.slug}`}
                  className="group flex flex-col border border-border bg-card p-5 h-full hover-lift">
                  <span className="flex items-center justify-between mb-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </span>
                  <span className="font-display text-xl">{m.name}</span>
                  <span className="mt-1 text-xs text-muted-foreground">{m.note}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lead magnet */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <PlaybookCta />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24 border-t border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionTitle overline="FAQ" title="Straight answers." />
          </div>
          <div className="lg:col-span-8">
            {kw.faqs.map((f, i) => (
              <details key={i} className="group border-b border-border py-5" data-testid={`keyword-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer font-medium text-lg list-none">
                  {f.q}
                  <ArrowRight className="w-4 h-4 text-primary rotate-90 group-open:-rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed pr-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="py-16 border-t border-border">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10">
            <p className="overline text-primary mb-6">Related services</p>
            <div className="flex flex-wrap gap-3" data-testid="keyword-related">
              {related.map((r) => (
                <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`keyword-related-${r.slug}`}
                  className="border border-border px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
                  {r.h1.replace(/\.$/, "")}
                </Link>
              ))}
              <Link to="/services" className="border border-primary/40 text-primary px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors" data-testid="keyword-all-services">
                All services →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground" data-testid="keyword-cta">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-6 opacity-80" />
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] max-w-3xl mx-auto">Ready when you are.</h2>
          <p className="mt-6 text-primary-foreground/85 text-lg max-w-xl mx-auto">
            Get an instant estimate — 60 seconds, no sales call, no email required to see the number.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/pricing" data-testid="keyword-bottom-quote-cta" className="inline-flex items-center gap-2 bg-background text-primary px-8 py-4 font-semibold hover:bg-background/90 active:scale-95 transition">
              Get My Instant Quote <Zap className="w-5 h-5" />
            </Link>
            <Link to="/start-project" data-testid="keyword-bottom-start-cta" className="inline-flex items-center gap-2 border border-primary-foreground/40 px-8 py-4 font-semibold hover:bg-primary-foreground/10 active:scale-95 transition">
              Start My Project <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
